import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import Breadcrumb from '../../components/Breadcrumb';
import PageHeader from '../../components/common/PageHeader';
import { selectUser } from '../../services/reducers/user';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import { API_URL } from '../../config/api';
import { Drawer } from '../../theme/components';

const MyAffiliateRequestsPage = () => {
  const user = useSelector(selectUser);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });
  
  // Filtros
  const [filters, setFilters] = useState({
    status: 'all',
    search: ''
  });

  // Estado do modal de cancelamento
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState(null);

  // Carregar solicitações
  useEffect(() => {
    loadRequests();
  }, [filters]);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams();
      
      if (filters.status !== 'all') {
        params.append('status', filters.status);
      }
      
      if (filters.search) {
        params.append('search', filters.search);
      }

      const response = await fetch(`${API_URL}/api/my-affiliate-requests?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        setRequests(data.data || []);
        setStats(data.stats || {
          total: 0,
          pending: 0,
          approved: 0,
          rejected: 0
        });
      } else {
        throw new Error(data.message || 'Erro ao carregar solicitações');
      }
      
    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar solicitações:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_URL}/api/my-affiliate-requests/${requestId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.success) {
        // Remover da lista local
        setRequests(prev => prev.filter(req => req.id !== requestId));
        
        // Atualizar estatísticas
        setStats(prev => ({
          ...prev,
          total: prev.total - 1,
          pending: prev.pending - 1
        }));

        alert('Solicitação cancelada com sucesso!');
        
        // Recarregar para garantir sincronização
        setTimeout(() => {
          loadRequests();
        }, 500);
        
      } else {
        throw new Error(data.message || 'Erro ao cancelar solicitação');
      }
      
    } catch (err) {
      setError(err.message);
      alert('Erro ao cancelar solicitação: ' + err.message);
    } finally {
      setActionLoading(false);
      setShowCancelModal(false);
      setRequestToCancel(null);
    }
  };

  const openRequestDetails = (request) => {
    setSelectedRequest(request);
    setShowDrawer(true);
  };

  const openCancelModal = (request) => {
    setRequestToCancel(request);
    setShowCancelModal(true);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'approved': { color: 'success', icon: 'solar:check-circle-bold', text: 'Aprovado' },
      'pending': { color: 'warning', icon: 'solar:clock-circle-bold', text: 'Pendente' },
      'rejected': { color: 'danger', icon: 'solar:close-circle-bold', text: 'Rejeitado' }
    };

    const config = statusConfig[status] || statusConfig['pending'];
    
    return (
      <span className={`badge bg-${config.color} text-white d-flex align-items-center gap-1`}>
        <Icon icon={config.icon} className="text-xs" />
        {config.text}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatPrice = (priceInCents) => {
    return CurrencyFormatter.centsToFormatted(priceInCents);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return 'solar:check-circle-bold';
      case 'rejected':
        return 'solar:close-circle-bold';
      default:
        return 'solar:clock-circle-bold';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'text-success-600';
      case 'rejected':
        return 'text-danger-600';
      default:
        return 'text-warning-600';
    }
  };

  return (
    <MasterLayout>
      <Breadcrumb 
        title="Minhas Solicitações" 
        items={[
          { label: 'Início', path: '/Dashboard' },
          { label: 'Afiliados', path: '/affiliate/my-affiliate-products' },
          { label: 'Minhas Solicitações', path: '/my-affiliate-requests' }
        ]}
      />

      {/* Header Section */}
      <PageHeader
        icon="mdi:clipboard-list"
        title="Minhas Solicitações"
        subtitle="Acompanhe todas as suas solicitações para se tornar afiliado"
      />

      <div className="dashboard-main-body">
        {/* Cards de Resumo - Padrão Dashboard */}
        <div className="row g-3" style={{ marginBottom: '24px' }}>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="flex-grow-1">
                    <h6 className="card-title text-muted mb-2" style={{ fontSize: '12px', fontWeight: '500' }}>Total</h6>
                    <span className="mb-1 d-block" style={{ fontSize: '20px', fontWeight: 'bold',  }}>
                      {stats.total}
                    </span>
                    <small className="text-primary" style={{ fontSize: '11px', fontWeight: '500' }}>
                      Solicitações enviadas
                    </small>
                  </div>
                  <div className="bg-primary bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                    <Icon icon="mdi:clipboard-list" className="text-primary" style={{ fontSize: '20px' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="flex-grow-1">
                    <h6 className="card-title text-muted mb-2" style={{ fontSize: '12px', fontWeight: '500' }}>Pendentes</h6>
                    <span className="mb-1 d-block" style={{ fontSize: '20px', fontWeight: 'bold',  }}>
                      {stats.pending}
                    </span>
                    <small className="text-warning" style={{ fontSize: '11px', fontWeight: '500' }}>
                      Aguardando aprovação
                    </small>
                  </div>
                  <div className="bg-warning bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                    <Icon icon="mdi:clock-outline" className="text-warning" style={{ fontSize: '20px' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="flex-grow-1">
                    <h6 className="card-title text-muted mb-2" style={{ fontSize: '12px', fontWeight: '500' }}>Aprovadas</h6>
                    <span className="mb-1 d-block" style={{ fontSize: '20px', fontWeight: 'bold',  }}>
                      {stats.approved}
                    </span>
                    <small className="text-success" style={{ fontSize: '11px', fontWeight: '500' }}>
                      Pronto para promover
                    </small>
                  </div>
                  <div className="bg-success bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                    <Icon icon="mdi:check-circle" className="text-success" style={{ fontSize: '20px' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
              <div className="card-body p-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="flex-grow-1">
                    <h6 className="card-title text-muted mb-2" style={{ fontSize: '12px', fontWeight: '500' }}>Rejeitadas</h6>
                    <span className="mb-1 d-block" style={{ fontSize: '20px', fontWeight: 'bold',  }}>
                      {stats.rejected}
                    </span>
                    <small className="text-danger" style={{ fontSize: '11px', fontWeight: '500' }}>
                      Não aprovadas
                    </small>
                  </div>
                  <div className="bg-danger bg-opacity-10 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '8px' }}>
                    <Icon icon="mdi:close-circle" className="text-danger" style={{ fontSize: '20px' }} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="card radius-12 mb-24">
          <div className="card-body p-20 ">
            <div className=" gap-3 d-flex flex-column justify-content-start flex-md-row align-items-md-end">
              <div className="col-md-4">
                <label className="form-label text-sm fw-medium">Status</label>
                <select
                  className="form-select"
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="all">Todos os Status</option>
                  <option value="pending">Pendente</option>
                  <option value="approved">Aprovado</option>
                  <option value="rejected">Rejeitado</option>
                </select>
              </div>
              
              <div className="col-md-6">
                <label className="form-label text-sm fw-medium">Buscar</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nome do produto ou produtor..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
              
              {/* <div className="col-md-2 d-flex "> */}
                <button
                  onClick={loadRequests}
                  className="btn btn-primary-600 text-white fw-medium w-100 icon-card"
                  style={{width: "100%", maxHeight: "44px" }}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Carregando...
                    </>
                  ) : (
                    <>
                      <Icon icon="solar:refresh-bold" className="me-2" />
                      Atualizar
                    </>
                  )}
                </button>
              {/* </div> */}
            </div>
          </div>
        </div>

        {/* Lista de Solicitações */}
        <div className="card radius-12">
          <div className="card-header border-bottom bg-base py-16 px-24">
            <div className="d-flex align-items-center justify-content-between">
              <span className='icon-card' style={{ fontSize: '16px', fontWeight: '600' }}>
                <Icon icon="solar:document-text-bold" className="me-2 text-primary-600"style={{minWidth: "22px", minHeight: "22px"}} />
                Minhas Solicitações de Afiliação
              </span>
              
              <div className="d-flex gap-2">
                <a 
                  href="#/store/store-products" 
                  className="btn btn-outline-primary-600 text-primary-600 btn-sm fw-medium icon-card"
                >
                  <Icon icon="solar:add-circle-bold" className="me-1" />
                  Nova Solicitação
                </a>
              </div>
            </div>
          </div>
          
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary mb-3" />
                <p className="text-muted">Carregando solicitações...</p>
              </div>
            ) : error ? (
              <div className="text-center py-5">
                <Icon icon="solar:close-circle-bold" className="text-danger mb-3" style={{ fontSize: '48px' }} />
                <h5 className="text-danger mb-2">Erro ao carregar solicitações</h5>
                <p className="text-muted mb-3">{error}</p>
                <button 
                  onClick={loadRequests}
                  className="btn btn-primary-600 text-white"
                >
                  Tentar Novamente
                </button>
              </div>
            ) : requests.length === 0 ? (
              <div className="text-center py-5">
                <Icon 
                  icon="solar:document-text-bold" 
                  className="text-secondary-light mb-3" 
                  style={{ fontSize: '64px' }} 
                />
                <h5 className="text-secondary-light mb-2">Nenhuma solicitação encontrada</h5>
                <p className="text-muted mb-3">
                  Você ainda não fez nenhuma solicitação de afiliação.
                </p>
                <a href="#/store/store-products" className="btn btn-primary-600 text-white">
                  <Icon icon="solar:eye-bold" className="me-2" />
                  Explorar Produtos na Loja
                </a>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead className="table-light">
                    <tr>
                      <th style={{ fontSize: '12px', fontWeight: '500' }} className="text-gray-300">Produto</th>
                      <th style={{ fontSize: '12px', fontWeight: '500' }} className="text-gray-300">Produtor</th>
                      <th style={{ fontSize: '12px', fontWeight: '500' }} className="text-gray-300">Comissão</th>
                      <th style={{ fontSize: '12px', fontWeight: '500' }} className="text-gray-300">Data</th>
                      <th style={{ fontSize: '12px', fontWeight: '500' }} className="text-gray-300">Status</th>
                      <th style={{ fontSize: '12px', fontWeight: '500' }} className="text-gray-300">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr key={request.id} style={{ padding: '16px 0' }}>
                        <td>
                          <div className="d-flex align-items-center gap-3">
                            <div className="w-40-px h-40-px bg-primary-50 text-primary-600 radius-8 d-flex align-items-center justify-content-center flex-shrink-0">
                              <Icon icon="solar:box-bold" />
                            </div>
                            <div>
                              <h6 className="text-sm fw-medium mb-0 line-height-1">
                                {request.product.name?.length > 30 
                                  ? `${request.product.name.substring(0, 30)}...` 
                                  : request.product.name}
                              </h6>
                              <div className="d-flex align-items-center gap-2 mt-1">
                                <span className="text-xs text-secondary-light">{request.product.code}</span>
                                <span className="badge bg-info-50 text-info-600 text-xs">
                                  {request.product.category}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <div className="w-32-px h-32-px bg-success-50 text-success-600 radius-8 d-flex align-items-center justify-content-center flex-shrink-0">
                              <Icon icon="solar:user-bold" />
                            </div>
                            <div>
                              <h6 className="text-sm fw-medium mb-0">{request.producer.name}</h6>
                              <span className="text-xs text-secondary-light">{request.producer.email}</span>
                            </div>
                          </div>
                        </td>
                        
                        <td>
                          <div className="d-flex flex-column">
                            <span className="text-sm fw-semibold text-warning-600">
                              {request.commission_rate}%
                            </span>
                            <small className="text-muted">
                              {formatPrice(Math.round((request.product.price * request.commission_rate) / 100))}
                            </small>
                          </div>
                        </td>
                        
                        <td>
                          <span className="text-sm text-secondary-light">
                            {formatDate(request.created_at)}
                          </span>
                        </td>
                        
                        <td className="text-center" style={{ verticalAlign: 'middle' }}>
                          {getStatusBadge(request.status)}
                        </td>
                        
                        <td className="text-center" style={{ verticalAlign: 'middle' }}>
                          <div className="d-flex gap-1 justify-content-center">
                            <button
                              onClick={() => openRequestDetails(request)}
                              className="btn btn-outline-info btn-sm icon-card"
                              title="Ver detalhes"
                            >
                              <Icon icon="solar:eye-bold" />
                            </button>
                            
                            {request.status === 'pending' && (
                              <button
                                onClick={() => openCancelModal(request)}
                                disabled={actionLoading}
                                className="btn btn-outline-danger btn-sm icon-card"
                                title="Cancelar solicitação"
                              >
                                <Icon icon="solar:trash-bin-minimalistic-bold" />
                              </button>
                            )}
                            
                            {request.status === 'approved' && (
                              <a
                                href={request.product.sales_page_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-success-600 text-white btn-sm icon-card"
                                title="Página de vendas"
                              >
                                <Icon icon="solar:link-bold" />
                              </a>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Drawer de Detalhes da Solicitação */}
      <Drawer
        show={showDrawer}
        onHide={() => {
          setShowDrawer(false);
          setSelectedRequest(null);
        }}
        title={selectedRequest ? `Detalhes da Solicitação - ${selectedRequest.product?.name || 'Solicitação'}` : 'Detalhes da Solicitação'}
        subtitle="Informações completas da sua solicitação de afiliação"
        icon="solar:document-text-bold"
        size="lg"
        footer={
          <>
            {selectedRequest?.status === 'pending' && (
              <Drawer.Button 
                variant="danger"
                icon="solar:trash-bin-minimalistic-bold"
                onClick={() => {
                  setShowDrawer(false);
                  openCancelModal(selectedRequest);
                }}
              >
                Cancelar Solicitação
              </Drawer.Button>
            )}
            
            {selectedRequest?.status === 'approved' && (
              <Drawer.Button 
                variant="success"
                icon="solar:link-bold"
                onClick={() => {
                  if (selectedRequest.product?.sales_page_url) {
                    window.open(selectedRequest.product.sales_page_url, '_blank', 'noopener,noreferrer');
                  }
                }}
              >
                Acessar Página de Vendas
              </Drawer.Button>
            )}
            
            <Drawer.Button 
              variant="secondary" 
              onClick={() => {
                setShowDrawer(false);
                setSelectedRequest(null);
              }}
            >
              Fechar
            </Drawer.Button>
          </>
        }
      >
        {selectedRequest ? (
          <>
            {/* Status da Solicitação */}
            <Drawer.Section title="Status da Solicitação" icon={getStatusIcon(selectedRequest.status)}>
              <div className="d-flex align-items-center gap-3 mb-3">
                {getStatusBadge(selectedRequest.status)}
                <div className="flex-grow-1">
                  {selectedRequest.status === 'pending' && (
                    <p className="text-muted mb-0 text-sm">
                      Sua solicitação foi enviada e está aguardando aprovação do produtor.
                    </p>
                  )}
                  {selectedRequest.status === 'approved' && (
                    <p className="text-success mb-0 text-sm">
                      Parabéns! Sua solicitação foi aprovada. Você já pode promover este produto.
                    </p>
                  )}
                  {selectedRequest.status === 'rejected' && (
                    <p className="text-danger mb-0 text-sm">
                      Sua solicitação foi rejeitada pelo produtor.
                    </p>
                  )}
                </div>
              </div>
            </Drawer.Section>

            <Drawer.Divider />

            {/* Dados do Produto */}
            <Drawer.Section title="Dados do Produto" icon="solar:box-bold">
              <Drawer.Row 
                label="Nome do Produto" 
                value={selectedRequest.product?.name || 'Não informado'}
                highlight
              />
              <Drawer.Row 
                label="Código" 
                value={selectedRequest.product?.code || 'Não informado'}
              />
              <Drawer.Row 
                label="Categoria" 
                value={selectedRequest.product?.category || 'Não informado'}
              />
              <Drawer.Row 
                label="Preço" 
                value={formatPrice(selectedRequest.product?.price || 0)}
                highlight
              />
            </Drawer.Section>

            <Drawer.Divider />

            {/* Dados do Produtor */}
            <Drawer.Section title="Dados do Produtor" icon="solar:user-bold">
              <Drawer.Row 
                label="Nome" 
                value={selectedRequest.producer?.name || 'Não informado'}
              />
              <Drawer.Row 
                label="Email" 
                value={selectedRequest.producer?.email || 'Não informado'}
              />
            </Drawer.Section>

            <Drawer.Divider />

            {/* Informações de Comissão */}
            <Drawer.Section title="Informações de Comissão" icon="mdi:percent">
              <Drawer.Row 
                label="Taxa de Comissão" 
                value={`${selectedRequest.commission_rate || 0}%`}
                highlight
              />
              <Drawer.Row 
                label="Comissão por Venda" 
                value={formatPrice(Math.round(((selectedRequest.product?.price || 0) * (selectedRequest.commission_rate || 0)) / 100))}
                highlight
              />
            </Drawer.Section>

            <Drawer.Divider />

            {/* Histórico */}
            <Drawer.Section title="Histórico" icon="solar:calendar-bold">
              <div className="d-flex align-items-center gap-3 mb-3">
                <div className="w-40-px h-40-px bg-info-50 text-info-600 radius-8 d-flex align-items-center justify-content-center">
                  <Icon icon="solar:calendar-add-bold" />
                </div>
                <div>
                  <p className="mb-0 fw-medium">Solicitação enviada</p>
                  <span className="text-sm text-secondary-light">
                    {formatDate(selectedRequest.created_at)}
                  </span>
                </div>
              </div>
              
              {selectedRequest.updated_at && selectedRequest.updated_at !== selectedRequest.created_at && (
                <div className="d-flex align-items-center gap-3">
                  <div className={`w-40-px h-40-px radius-8 d-flex align-items-center justify-content-center ${
                    selectedRequest.status === 'approved' ? 'bg-success-50 text-success-600' : 'bg-danger-50 text-danger-600'
                  }`}>
                    <Icon icon={selectedRequest.status === 'approved' ? 'solar:check-circle-bold' : 'solar:close-circle-bold'} />
                  </div>
                  <div>
                    <p className="mb-0 fw-medium">
                      {selectedRequest.status === 'approved' ? 'Solicitação aprovada' : 'Solicitação rejeitada'}
                    </p>
                    <span className="text-sm text-secondary-light">
                      {formatDate(selectedRequest.updated_at)}
                    </span>
                  </div>
                </div>
              )}
            </Drawer.Section>
          </>
        ) : (
          <div className="text-center py-5">
            <div className="spinner-border text-primary mb-3" />
            <p className="text-muted">Carregando detalhes...</p>
          </div>
        )}
      </Drawer>

      {/* Modal de Confirmação de Cancelamento */}
      {showCancelModal && requestToCancel && (
        <div 
          className="modal fade show d-block" 
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onClick={() => setShowCancelModal(false)}
        >
          <div 
            className="modal-dialog modal-dialog-centered"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-bold text-danger">
                  <Icon icon="solar:trash-bin-minimalistic-bold" className="me-2" />
                  Cancelar Solicitação
                </h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowCancelModal(false)}
                />
              </div>
              
              <div className="modal-body p-24">
                <div className="text-center">
                  <Icon 
                    icon="solar:danger-triangle-bold" 
                    className="text-warning mb-3" 
                    style={{ fontSize: '48px' }} 
                  />
                  
                  <h5 className="fw-bold mb-3">Tem certeza?</h5>
                  
                  <p className="text-muted mb-4">
                    Você está prestes a cancelar sua solicitação de afiliação para o produto:
                  </p>
                  
                  <div className="card radius-8 mb-4">
                    <div className="card-body p-16">
                      <h6 className="fw-bold text-primary-600 mb-1">
                        {requestToCancel.product.name}
                      </h6>
                      <span className="text-sm text-secondary-light">
                        Produtor: {requestToCancel.producer.name}
                      </span>
                    </div>
                  </div>
                  
                  <p className="text-muted text-sm">
                    Esta ação não pode ser desfeita. Você precisará fazer uma nova solicitação se mudar de ideia.
                  </p>
                </div>
              </div>
              
              <div className="modal-footer border-top">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowCancelModal(false)}
                >
                  Manter Solicitação
                </button>
                
                <button
                  onClick={() => handleCancelRequest(requestToCancel.id)}
                  disabled={actionLoading}
                  className="btn btn-danger"
                >
                  {actionLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Cancelando...
                    </>
                  ) : (
                    <>
                      <Icon icon="solar:trash-bin-minimalistic-bold" className="me-2" />
                      Sim, Cancelar
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </MasterLayout>
  );
};

export default MyAffiliateRequestsPage;